import type { ReactElement, ReactNode, SyntheticEvent } from 'react';

import { Tooltip } from '@scaleaq/scaleui';

import { StyledCardSidebar, StyledCardSidebarButton } from './cardSidebar.styles.ts';

/**
 * Keeps clicks inside the sidebar, on the buttons and the empty area between them, from reaching
 * the card's own click or link handling.
 */
const stopPropagation = (e: SyntheticEvent) => e.stopPropagation();

type CardSidebarProps = {
  children: ReactNode;
};

/**
 * Hover menu on the right edge of a card. Place it as the last direct child of a card that is
 * `position: relative`; compose it from `CardSidebarButton` and `CardSidebarSpacer`.
 */
export const CardSidebar = ({ children }: CardSidebarProps) => (
  <StyledCardSidebar onClick={stopPropagation} onMouseDown={stopPropagation}>
    {children}
  </StyledCardSidebar>
);

type CardSidebarButtonProps = {
  /** Shown as tooltip and used as the accessible name. */
  label: string;
  icon: ReactElement;
  onClick: () => void;
  /** Destructive actions, such as delete, are drawn in the alarm colour. */
  destructive?: boolean;
  disabled?: boolean;
};

export const CardSidebarButton = ({
  label,
  icon,
  onClick,
  destructive = false,
  disabled = false,
}: CardSidebarButtonProps) => (
  <Tooltip tooltip={label} placement='left'>
    <StyledCardSidebarButton
      aria-label={label}
      size='small'
      Icon={icon}
      $destructive={destructive}
      onClick={onClick}
      disabled={disabled}
    />
  </Tooltip>
);
